// @file Store for all whiteboard upload state and logic in Sandbox
import { ContentPickerSource } from '@@/bits/content_picker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { FILE_TOO_LARGE_ERROR, startUpload, type UploadJob } from '@@/bits/uploader'
import { deleteShapeMetadata } from '@@/bits/whiteboard'
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '@@/bits/whiteboard_no_dependencies'
import {
  calculateShapeDimensions,
  constrainShapePositionToCanvas,
  DEFAULT_ASPECT_RATIO,
  DEFAULT_POSITION,
  DEFAULT_WIDTH,
} from '@@/bits/whiteboard_upload'
import { SnackbarNotificationType } from '@@/enums'
import { useGlobalAlertDialogStore } from '@@/pinia/global_alert_dialog'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { Post } from '@@/types'
import type { PadletAttachmentShape } from '@padlet/whiteboard-schema'
import { uniqueId } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import type { Editor, JsonObject, TLShapeId } from 'tldraw'
import { createShapeId, TAB_ID } from 'tldraw'
import { ref } from 'vue'

export const useWhiteboardUploadStore = defineStore('whiteboardUpload', () => {
  const surfaceStore = useSurfaceStore()
  const globalAlertDialogStore = useGlobalAlertDialogStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfacePostsStore = useSurfacePostsStore()
  const fileThatExceededSize = ref()

  /**
   * ATTACHMENTS
   */

  type UploadKey = string
  interface Upload {
    uploadKey: UploadKey
    file?: File | null
    fileName: string
    progress: number | null
    done: boolean
    type: 'file' | 'link'
    uploadJob?: UploadJob
    shapeId?: TLShapeId
    postCid?: string
  }
  const uploads = ref<Record<UploadKey, Upload>>({})
  const progressTimerById = ref<Record<UploadKey, ReturnType<typeof setInterval>>>({})
  const ONE_MB = 1024 * 1024

  function getNextUploadKey(): UploadKey {
    return uniqueId('upload-')
  }

  function startUploading(file: File, uploadKey: UploadKey, uploadJob?: UploadJob): void {
    const initialUploadProgress = 0.3
    uploads.value[uploadKey] = {
      uploadKey,
      fileName: file.name,
      progress: initialUploadProgress,
      done: false,
      type: 'file',
      uploadJob,
    }
    uploads.value = { ...uploads.value }
    const fileSize = file.size
    // Calculate expected time and upload based on that
    const expectedTime = fileSize !== null ? fileSize / (4 * ONE_MB) + 4 : 1
    let timeSpent = 0
    const progressTimer = setInterval(() => {
      timeSpent += 1
      if (timeSpent >= expectedTime) {
        clearInterval(progressTimer)
        uploads.value[uploadKey].progress = 1
        uploads.value = { ...uploads.value }
        return
      }
      const uploadProgress = initialUploadProgress + (timeSpent / expectedTime) * 0.7
      uploads.value[uploadKey].progress = uploadProgress
      uploads.value = { ...uploads.value }
    }, 1000)

    progressTimerById.value[uploadKey] = progressTimer
  }

  function finishUploadingOld(uploadKey: UploadKey): void {
    clearInterval(progressTimerById.value[uploadKey])
    uploads.value[uploadKey].progress = null
    uploads.value[uploadKey].done = true
    uploads.value = { ...uploads.value }
    fileThatExceededSize.value = null
  }

  function cancelUploadOld(uploadKey: UploadKey): void {
    clearInterval(progressTimerById.value[uploadKey])
    const uploadJob = uploads.value[uploadKey].uploadJob
    if (uploadJob != null) {
      uploadJob.cancel()
    }
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete uploads.value[uploadKey]
    uploads.value = { ...uploads.value }
  }

  function startUploadingLinkOld(link: string, uploadKey: UploadKey): void {
    uploads.value[uploadKey] = {
      uploadKey,
      fileName: link,
      progress: null,
      done: false,
      type: 'link',
    }
    uploads.value = { ...uploads.value }
  }

  function startNewFileUploadOld(
    file: File,
    options: {
      contentSource: ContentPickerSource
      trimVideoAfterUpload?: { start: number; end: number }
    },
  ): UploadKey {
    const uploadJob = startUpload(file, {
      maxFileSize: surfaceStore.uploadLimit,
      trimVideoAfterUpload: options.trimVideoAfterUpload,
    })
    const uploadKey = getNextUploadKey()
    startUploading(file, uploadKey, uploadJob)
    uploadJob.on('start', () => {
      startUploading(file, uploadKey)
    })
    uploadJob.on('done', (payload) => {
      finishUploadingOld(uploadKey)
      useSurfacePostsStore().createPostImmediately({
        attributes: {
          // In the old upload flow, the shape is only created after the upload is done.
          // See WhiteboardAttachmentPicker.vue#watch(newestPostId)
          // In order to pass the `contentSource` here to that watcher, we need to store
          // it temporarily in `post.subject`. This is just a hack and once we release
          // the new upload flow we will pass the `contentSource` in a more direct way.
          subject: options?.contentSource,
          attachment: payload.url,
          wish_content: {
            attachment_props: {
              url: payload.url,
            },
            is_processed: false,
          },
        },
      })
    })
    uploadJob.on('error', (response) => {
      cancelUploadOld(uploadKey)

      if (response.error === FILE_TOO_LARGE_ERROR) {
        fileThatExceededSize.value = file
        surfaceStore.showOverFileSizeModal({ file })
      } else {
        globalAlertDialogStore.openAlertDialog({
          title: __('There was an error loading the file'),
          body: __('Sorry, there was an error uploading your file.'),
        })
      }
    })
    return uploadKey
  }

  function startNewUrlUploadOld(
    url: string,
    options: { altText?: string; contentSource: ContentPickerSource },
  ): UploadKey {
    const uploadKey = url
    const fileName = url.split('/').pop() ?? url
    startUploadingLinkOld(options?.altText ?? fileName, uploadKey)
    useSurfacePostsStore().createPostImmediately({
      attributes: {
        // In the old upload flow, the shape is only created after the upload is done.
        // See WhiteboardAttachmentPicker.vue#watch(newestPostId)
        // In order to pass the `contentSource` here to that watcher, we need to store
        // it temporarily in `post.subject`. This is just a hack and once we release
        // the new upload flow we will pass the `contentSource` in a more direct way.
        subject: options?.contentSource,
        attachment: url,
        wish_content: {
          attachment_props: {
            url,
            alternative_text: options?.altText,
          },
          is_processed: false,
        },
      },
    })
    return uploadKey
  }

  // #region Uploading animation
  interface UploadingAttachmentShapeOptions {
    contentSource: ContentPickerSource
    aspectRatio?: number
    width?: number
    height?: number
    x?: number
    y?: number
  }

  function createUploadingAttachmentShape({
    uploadKey,
    file,
    options,
  }: {
    uploadKey: UploadKey
    file?: File
    options?: UploadingAttachmentShapeOptions
  }): TLShapeId | null {
    const editor = getEditorObject()
    if (editor == null) return null
    const shapeId = createShapeId()
    const width = options?.width ?? DEFAULT_WIDTH
    const height = options?.height ?? width / (options?.aspectRatio ?? DEFAULT_ASPECT_RATIO)

    const meta: Partial<JsonObject> = {
      uploadKey,
      isUploaded: false,
      tabId: TAB_ID,
      contentSource: options?.contentSource ?? null,
    }

    const isAudioRecording = meta.contentSource === ContentPickerSource.AudioRecorder
    const isAudioUpload = file?.type.startsWith('audio/') ?? false
    if (isAppUsing('audioPlayerV2Sandbox') && (isAudioRecording || isAudioUpload)) {
      meta.audioPlayerVersion = 2
    }

    editor.createShape({
      id: shapeId,
      type: 'padlet-attachment-shape',
      x: options?.x ?? CANVAS_WIDTH / 2 - width / 2, // Center the shape
      y: options?.y ?? CANVAS_HEIGHT / 2 - height / 2, // Center the shape
      props: {
        postId: 0,
        w: width,
        h: height,
      },
      meta,
    })

    return shapeId
  }

  async function updateUploadingAttachmentShape(editor: Editor, post: Post): Promise<void> {
    const upload = Object.values(uploads.value).find((upload) => upload.postCid === post.cid)
    if (upload === undefined) return
    const shapeId = upload.shapeId as TLShapeId
    if (shapeId == null) return
    const shape = editor.getShape(shapeId) as PadletAttachmentShape | null
    if (shape == null) return
    let shapeDimensionsProps = {}

    // We don't want to resize local images attachments after uploading
    if (upload.type === 'link' || (upload.file != null && !upload.file.type.startsWith('image/'))) {
      const shapeDimensions = await calculateShapeDimensions(post, shape)
      shapeDimensionsProps = shapeDimensions != null ? { w: shapeDimensions.width, h: shapeDimensions.height } : {}
    }

    const { newX, newY } = constrainShapePositionToCanvas(shape, shapeDimensionsProps)

    // Update the shape dimensions first to make sure the shape is resized before the attachment is rendered to avoid flashing
    editor.updateShape({
      ...shape,
      x: newX,
      y: newY,
      props: {
        ...shape.props,
        ...shapeDimensionsProps,
      },
    })
    void surfacePostsStore.savePost({
      attributes: {
        ...post,
        top: newY,
        left: newX,
      },
    })
    // wait for the shape to be resized before rendering the attachment
    setTimeout(() => {
      editor.updateShape({
        ...shape,
        x: newX,
        y: newY,
        props: {
          ...shape.props,
          postId: post.id,
          ...shapeDimensionsProps,
        },
      })
      finishUploading(upload.uploadKey)
    }, 1000)
  }

  function startFileUploading({
    file,
    uploadKey,
    uploadJob,
    shapeId,
  }: {
    file: File
    uploadKey: UploadKey
    uploadJob: UploadJob
    shapeId: TLShapeId
  }): void {
    const initialUploadProgress = 0.3
    uploads.value[uploadKey] = {
      uploadKey,
      file,
      fileName: file.name,
      progress: initialUploadProgress,
      done: false,
      type: 'file',
      uploadJob,
      shapeId,
    }
    uploads.value = { ...uploads.value }
    const fileSize = file.size
    // Calculate expected time and upload based on that
    const expectedTime = fileSize !== null ? fileSize / (4 * ONE_MB) + 4 : 1
    let timeSpent = 0
    const progressTimer = setInterval(() => {
      timeSpent += 1
      if (timeSpent >= expectedTime) {
        clearInterval(progressTimer)
        uploads.value[uploadKey].progress = 1
        uploads.value = { ...uploads.value }
        return
      }
      const uploadProgress = initialUploadProgress + (timeSpent / expectedTime) * 0.7
      uploads.value[uploadKey].progress = uploadProgress
      uploads.value = { ...uploads.value }
    }, 1000)

    progressTimerById.value[uploadKey] = progressTimer
  }

  function addPostCidToUpload(uploadKey: UploadKey, postCid: string): void {
    uploads.value[uploadKey] = {
      ...uploads.value[uploadKey],
      postCid,
    }
    uploads.value = { ...uploads.value }
  }

  function startUploadingLink(link: string, uploadKey: UploadKey, shapeId: TLShapeId): void {
    uploads.value[uploadKey] = {
      uploadKey,
      fileName: link,
      progress: null,
      done: false,
      type: 'link',
      shapeId,
    }
    uploads.value = { ...uploads.value }
  }

  function cancelUpload(uploadKey: UploadKey): void {
    const { [uploadKey]: upload, ...remainingUploads } = uploads.value
    if (upload == null) return
    // Cancel upload
    clearInterval(progressTimerById.value[uploadKey])
    const uploadJob = upload.uploadJob
    if (uploadJob != null) {
      uploadJob.cancel()
    }
    // Remove upload from the uploads object
    uploads.value = remainingUploads
    // Delete shape
    const shapeId = upload.shapeId as TLShapeId
    if (shapeId == null) return
    const editor = getEditorObject()
    if (editor == null) return
    const shape = editor.getShape(shapeId)
    if (shape == null) return
    editor.deleteShape(shapeId)
  }

  function finishUploading(uploadKey: UploadKey): void {
    const { [uploadKey]: upload, ...remainingUploads } = uploads.value
    if (upload == null) return
    clearInterval(progressTimerById.value[uploadKey])
    uploads.value = remainingUploads
    fileThatExceededSize.value = null

    const shapeId = upload.shapeId as TLShapeId
    if (shapeId == null) return
    const editor = getEditorObject()
    if (editor == null) return
    const shape = editor.getShape(shapeId)
    if (shape == null) return
    deleteShapeMetadata(shape.id, ['isUploaded', 'tabId', 'uploadKey'])
  }

  async function startNewFileUpload(
    file: File,
    options: UploadingAttachmentShapeOptions & {
      hasBeenRetried?: boolean
      trimVideoAfterUpload?: { start: number; end: number }
    },
  ): Promise<void> {
    const mergedOptions = {
      aspectRatio: DEFAULT_ASPECT_RATIO,
      width: DEFAULT_WIDTH,
      height: DEFAULT_WIDTH / DEFAULT_ASPECT_RATIO,
      x: DEFAULT_POSITION.x,
      y: DEFAULT_POSITION.y,
      hasBeenRetried: false,
      trimVideoAfterUpload: undefined,
      ...options,
    }
    const uploadJob = startUpload(file, {
      maxFileSize: surfaceStore.uploadLimit,
      // TOOD: if trimming, startFileUploading/startUploading should expect a longer processing duration.
      trimVideoAfterUpload: mergedOptions.trimVideoAfterUpload,
    })
    const uploadKey = getNextUploadKey()
    const shapeId = createUploadingAttachmentShape({
      uploadKey,
      file,
      options: mergedOptions,
    })
    if (shapeId == null) return
    startFileUploading({ file, uploadKey, uploadJob, shapeId })

    uploadJob.on('done', (payload) => {
      const cid = uniqueId('c_new')
      addPostCidToUpload(uploadKey, cid)
      useSurfacePostsStore().createPostImmediately({
        attributes: {
          cid,
          attachment: payload.url,
          wish_content: {
            attachment_props: {
              url: payload.url,
            },
            is_processed: false,
          },
        },
      })
    })
    uploadJob.on('error', (response) => {
      cancelUpload(uploadKey)

      if (response.error === FILE_TOO_LARGE_ERROR) {
        fileThatExceededSize.value = file
        surfaceStore.showOverFileSizeModal({ file })
      } else {
        if (!mergedOptions.hasBeenRetried) {
          globalSnackbarStore.setSnackbar({
            message: __('Retrying upload... please keep this page open'),
          })
          void startNewFileUpload(file, { ...mergedOptions, hasBeenRetried: true })
        } else {
          globalSnackbarStore.setSnackbar({
            message: __('Sorry, there was an error uploading your file'),
            notificationType: SnackbarNotificationType.error,
          })
        }
      }
    })
  }

  function startNewUrlUpload(url: string, options?: { altText?: string; contentSource: ContentPickerSource }): void {
    const uploadKey = url
    const fileName = url.split('/').pop() ?? url
    const shapeId = createUploadingAttachmentShape({
      uploadKey,
      options: { contentSource: options?.contentSource as ContentPickerSource },
    })
    if (shapeId == null) return
    startUploadingLink(options?.altText ?? fileName, uploadKey, shapeId)
    const cid = uniqueId('c_new')
    addPostCidToUpload(uploadKey, cid)
    useSurfacePostsStore().createPostImmediately({
      attributes: {
        cid,
        attachment: url,
        wish_content: {
          attachment_props: {
            url,
            alternative_text: options?.altText,
          },
          is_processed: false,
        },
      },
    })
  }
  // #endregion

  return {
    /* ATTACHMENT */
    startUploading,
    startUploadingLinkOld,
    finishUploadingOld,
    cancelUploadOld,
    uploads,
    getNextUploadKey,
    startNewFileUploadOld,
    startNewUrlUploadOld,
    fileThatExceededSize,
    startNewFileUpload,
    updateUploadingAttachmentShape,
    startNewUrlUpload,
    cancelUpload,
  }
})
